import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import '../styles/auto-hide-nav.css';

interface AutoHideBottomNavProps {
  children: React.ReactNode;
  className?: string;
}

const AutoHideBottomNav: React.FC<AutoHideBottomNavProps> = ({ children, className = '' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showHandle, setShowHandle] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const touchStartY = useRef<number | null>(null);

  // Scroll detection logic
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDiff = currentScrollY - lastScrollY.current;
    
    // Clear any existing timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Scrolling down (hide nav)
    if (scrollDiff > 10 && currentScrollY > 100) {
      setIsVisible(false);
      setShowHandle(true);
    } 
    // Scrolling up (show nav)
    else if (scrollDiff < -5) {
      setIsVisible(true);
      setShowHandle(false);
    }
    // At the top of the page
    else if (currentScrollY < 50) {
      setIsVisible(true);
      setShowHandle(false);
    }

    lastScrollY.current = currentScrollY;
  }, []);

  // Touch handling for better mobile experience
  const handleTouchStart = (e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStartY.current) return;
    
    const touchDiff = e.touches[0].clientY - touchStartY.current;
    
    // Swiping up (hide nav)
    if (touchDiff < -30 && window.scrollY > 100) {
      setIsVisible(false);
      setShowHandle(true);
    }
    // Swiping down (show nav)
    else if (touchDiff > 30) {
      setIsVisible(true);
      setShowHandle(false);
    }
  }, []);

  const handleTouchEnd = () => {
    touchStartY.current = null;
  };

  // Handle click on the floating arrow
  const handleArrowClick = () => {
    setIsVisible(true);
    setShowHandle(false);
  };

  useEffect(() => {
    // Add scroll listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [handleScroll, handleTouchMove]);

  // Manage body class for proper spacing
  useEffect(() => {
    if (!isVisible) {
      document.body.classList.add('nav-auto-hidden');
    } else {
      document.body.classList.remove('nav-auto-hidden');
    }

    return () => {
      document.body.classList.remove('nav-auto-hidden');
    };
  }, [isVisible]);

  return (
    <>
      {/* Bottom Navigation Container */}
      <div
        className={`${className} auto-hide-nav ${isVisible ? 'nav-visible' : 'nav-hidden'}`}
        aria-hidden={!isVisible}
      >
        {children}
      </div>

      {/* Floating Arrow Handle */}
      {showHandle && (
        <button
          onClick={handleArrowClick}
          className="floating-arrow-handle"
          aria-label="Show navigation menu"
          role="button"
          tabIndex={0}
        >
          <ChevronUpIcon className="w-5 h-5" />
        </button>
      )}

    </>
  );
};

export default AutoHideBottomNav;